import React, { useCallback, useState } from "react";
import {
  HiCheck,
  HiChevronDown,
  HiChevronLeft,
  HiChevronRight,
  HiOutlineDownload,
  HiOutlineQuestionMarkCircle,
  HiOutlineRefresh,
  HiArrowRight,
  HiOutlineBookOpen,
  HiOutlineExternalLink,
  HiOutlineGlobeAlt,
} from "react-icons/hi";
import { classNames } from "Utils/utils";
import { getCopilotReducedModeCopy } from "../utils/copilotDegradedMode";
import {
  formatChatTimestamp,
  normalizeClarifyQuestions,
  parseClarifyUserContent,
  resolveClarifyQuestionId,
  sanitizeClarifyAnswers,
} from "../utils/vertex360CopilotChatUtils";
import { downloadCopilotTableCsv } from "../utils/vertex360CopilotCsvExport";
import {
  filterDisplayColumns,
  formatColumnLabel,
  formatTableCell,
} from "../utils/vertex360CopilotTableFormat";
import { VERTEX360_COPILOT_THEME } from "../constants/vertex360CopilotTheme";
import { useTypewriterReveal } from "../hooks/useTypewriterReveal";
import Vertex360CopilotAiIcon from "./Vertex360CopilotAiIcon";
import MarkdownMessage from "./MarkdownMessage";
import UserAvatar from "./UserAvatar";
import FollowUpChips from "./FollowUpChips";
import MessageActions from "./MessageActions";
import RenderGraph from "./RenderGraph";

const StreamingCursor = () => (
  <span
    className="ml-0.5 inline-block h-[1.1em] w-[2px] animate-pulse bg-[#4B6CB7] align-text-bottom"
    aria-hidden="true"
  />
);

const DownloadCsvButton = ({ render }) => {
  const handleDownloadCsv = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCopilotTableCsv(render, `vertex360-copilot-table-${stamp}.csv`);
  };

  return (
    <button
      type="button"
      onClick={handleDownloadCsv}
      className={classNames(
        "copilot-prompt-action inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1",
        "text-[11.5px] font-semibold text-slate-600 hover:text-[#4B6CB7]",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4B6CB7]/40"
      )}
    >
      <HiOutlineDownload
        className="h-3.5 w-3.5 text-[#4B6CB7]/70"
        aria-hidden="true"
      />
      Download CSV
    </button>
  );
};

/** Columns that can be long — cap width + truncate; full value on hover. */
const WIDE_TABLE_COLUMNS = new Set([
  "availability",
  "key_strengths",
  "strengths",
  "address",
  "email",
  "contact_email",
]);

/**
 * @param {string} column
 * @returns {boolean}
 */
const isWideTableColumn = (column) => {
  const bare = String(column || "")
    .split(".")
    .pop()
    .toLowerCase();
  return (
    WIDE_TABLE_COLUMNS.has(bare) ||
    bare.endsWith("_availability") ||
    bare.endsWith("_strengths")
  );
};

/** Accordion table: module parent rows expand to show child permissions. */
const RenderAccordionTable = ({ render, showDownload = true }) => {
  const groups = Array.isArray(render?.groups) ? render.groups : [];
  const columnMeta =
    render?.column_meta && typeof render.column_meta === "object"
      ? render.column_meta
      : null;
  const columns = ["permission_name", "permission_type"];
  const [openIds, setOpenIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!groups.length) return null;

  return (
    <div className="mt-2">
      {showDownload ? (
        <div className="mb-1.5 flex items-center justify-end">
          <DownloadCsvButton render={render} />
        </div>
      ) : null}
      <div className="max-h-80 overflow-auto rounded-lg border border-slate-200">
        <table className="w-max min-w-full border-collapse text-left text-[12.5px]">
          <thead className="sticky top-0 z-[1] bg-slate-50 text-slate-600">
            <tr>
              <th className="w-8 px-2 py-1.5" aria-hidden />
              {columns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-3 py-1.5 font-semibold"
                >
                  {formatColumnLabel(col, columnMeta)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group, gIdx) => {
              const id = String(group?.permission_name || gIdx);
              const children = Array.isArray(group?.children)
                ? group.children
                : [];
              const isOpen = openIds.has(id);
              const canExpand = children.length > 0;
              return (
                <React.Fragment key={id}>
                  <tr
                    className={classNames(
                      "border-t border-slate-100 bg-slate-50/80",
                      canExpand && "cursor-pointer hover:bg-slate-100/90"
                    )}
                    onClick={() => canExpand && toggle(id)}
                    onKeyDown={(e) => {
                      if (!canExpand) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(id);
                      }
                    }}
                    tabIndex={canExpand ? 0 : undefined}
                    aria-expanded={canExpand ? isOpen : undefined}
                  >
                    <td className="px-2 py-1.5 text-slate-500">
                      {canExpand ? (
                        isOpen ? (
                          <HiChevronDown className="h-4 w-4" />
                        ) : (
                          <HiChevronRight className="h-4 w-4" />
                        )
                      ) : null}
                    </td>
                    {columns.map((col) => {
                      const text = formatTableCell(
                        group?.[col],
                        col,
                        columnMeta
                      );
                      return (
                        <td
                          key={col}
                          className="whitespace-nowrap px-3 py-1.5 font-medium text-slate-800"
                        >
                          {text}
                          {col === "permission_name" && canExpand ? (
                            <span className="ml-1.5 text-[11px] font-normal text-slate-500">
                              ({children.length})
                            </span>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                  {isOpen
                    ? children.map((child, cIdx) => (
                        <tr
                          key={`${id}-${cIdx}`}
                          className="border-t border-slate-100 bg-white"
                        >
                          <td className="px-2 py-1.5" />
                          {columns.map((col) => (
                            <td
                              key={col}
                              className="whitespace-nowrap px-3 py-1.5 pl-6 text-slate-700"
                            >
                              {formatTableCell(child?.[col], col, columnMeta)}
                            </td>
                          ))}
                        </tr>
                      ))
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Render a data table produced by the SQL lane (rows are UI-only, never sent to the LLM). */
const RenderTable = ({ render, showDownload = true }) => {
  const isAccordion =
    render?.ui === "accordion" &&
    Array.isArray(render?.groups) &&
    render.groups.length > 0;

  const columns = filterDisplayColumns(
    Array.isArray(render?.columns) ? render.columns : []
  );
  const rows = Array.isArray(render?.rows) ? render.rows : [];
  // Schema-baked labels/types from the AI service (deploy-time schema gen).
  const columnMeta =
    render?.column_meta && typeof render.column_meta === "object"
      ? render.column_meta
      : null;
  const displayColumns = columns.filter((col) => col !== "row_kind");

  if (isAccordion) {
    return (
      <RenderAccordionTable render={render} showDownload={showDownload} />
    );
  }

  if (!displayColumns.length || !rows.length) return null;

  return (
    <div className="mt-2">
      {showDownload ? (
        <div className="mb-1.5 flex items-center justify-end">
          <DownloadCsvButton render={render} />
        </div>
      ) : null}
      <div className="max-h-72 overflow-auto rounded-lg border border-slate-200">
        {/* w-max + nowrap: scroll horizontally instead of crushing columns. */}
        <table className="w-max min-w-full border-collapse text-left text-[12.5px]">
          <thead className="sticky top-0 z-[1] bg-slate-50 text-slate-600">
            <tr>
              {displayColumns.map((col) => (
                <th
                  key={col}
                  className="whitespace-nowrap px-3 py-1.5 font-semibold"
                >
                  {formatColumnLabel(col, columnMeta)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-t border-slate-100">
                {displayColumns.map((col) => {
                  const text = formatTableCell(row?.[col], col, columnMeta);
                  const wide = isWideTableColumn(col);
                  return (
                    <td
                      key={col}
                      className={
                        wide
                          ? "max-w-[220px] truncate whitespace-nowrap px-3 py-1.5 text-slate-700"
                          : "whitespace-nowrap px-3 py-1.5 text-slate-700"
                      }
                      title={wide && text && text !== "—" ? text : undefined}
                    >
                      {text}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * A SQL data result. The table is always available; when the AI service
 * attaches a relationship graph (`render.graph`) we offer a Table/Graph toggle
 * and default to whichever view the backend chose (`render.default_view`).
 * If the graph fails to render we transparently fall back to the table.
 */
const RenderResult = ({ render }) => {
  const graph = render?.graph;
  const hasGraph =
    graph &&
    Array.isArray(graph.nodes) &&
    graph.nodes.length >= 2 &&
    Array.isArray(graph.edges) &&
    graph.edges.length >= 1;

  const [graphUnavailable, setGraphUnavailable] = useState(false);
  const [view, setView] = useState(
    render?.default_view === "graph" ? "graph" : "table"
  );
  const handleGraphUnavailable = useCallback(() => setGraphUnavailable(true), []);

  if (!hasGraph || graphUnavailable) {
    return <RenderTable render={render} />;
  }

  const showGraph = view === "graph";
  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div
          className="inline-flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-50 p-0.5"
          role="group"
          aria-label="Choose result view"
        >
          {[
            { id: "table", label: "Table" },
            { id: "graph", label: "Graph" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={view === tab.id}
              onClick={() => setView(tab.id)}
              className={classNames(
                "rounded-md px-3 py-1 text-[12px] font-semibold transition-colors",
                view === tab.id
                  ? "bg-white text-[#293F6A] shadow-sm"
                  : "text-slate-500 hover:text-[#4B6CB7]"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {!showGraph ? <DownloadCsvButton render={render} /> : null}
      </div>
      {showGraph ? (
        <RenderGraph graph={graph} onUnavailable={handleGraphUnavailable} />
      ) : (
        <RenderTable render={render} showDownload={false} />
      )}
    </div>
  );
};

/**
 * Disambiguation options (Participant / Worker / Type a role).
 * Options with ``allow_input`` render a text field so the user can type a
 * Spatie role (Manager, Super Admin, Support Coordinator, custom roles, …).
 */
const ClarifyOptions = ({ options, onSelect, selectedId, selectedLabel }) => {
  const [typedRole, setTypedRole] = useState("");
  if (!Array.isArray(options) || !options.length) return null;
  const hasSelection = Boolean(selectedId);

  const submitTyped = (option) => {
    const value = typedRole.trim();
    if (!value || hasSelection) return;
    onSelect?.({
      ...option,
      typedLabel: value,
      label: value,
    });
  };

  return (
    <div className="mt-2.5 overflow-hidden rounded-xl border border-[#DCE6FA] bg-gradient-to-b from-[#F7FAFF] to-white shadow-[0_6px_20px_-14px_rgba(41,63,106,0.35)]">
      <div className="flex items-center gap-2 border-b border-[#E7EEFB] px-3.5 py-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[#ECF0FD] text-[#4B6CB7]">
          <HiOutlineQuestionMarkCircle className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4B6CB7]">
          {hasSelection ? "Your selection" : "Select one to continue"}
        </p>
      </div>

      <div className="flex flex-col gap-1.5 p-2">
        {options.map((option, index) => {
          const isSelected =
            hasSelection && String(option.id) === String(selectedId);
          const isDimmed = hasSelection && !isSelected;
          const allowInput = Boolean(option.allow_input || option.allowInput);

          if (allowInput) {
            const displayLabel =
              isSelected && selectedLabel ? selectedLabel : option.label;
            return (
              <div
                key={option.id}
                className={classNames(
                  "box-border flex items-center gap-2.5 rounded-lg border px-3 py-2 transition-all duration-150",
                  isSelected
                    ? "border-solid border-[#8fa3d4] bg-[#ECF0FD] text-[#293F6A] shadow-sm"
                    : "border-transparent bg-white shadow-[0_1px_2px_rgba(41,63,106,0.06)] ring-1 ring-slate-200/70",
                  isDimmed && "opacity-55 ring-0",
                  hasSelection && "pointer-events-none"
                )}
              >
                <span
                  className={classNames(
                    "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[12px] font-bold",
                    isSelected
                      ? "bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white"
                      : "bg-[#ECF0FD] text-[#4B6CB7]"
                  )}
                >
                  {isSelected ? (
                    <HiCheck className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    `${index + 1}`
                  )}
                </span>
                {isSelected ? (
                  <>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#293F6A]">
                      {displayLabel}
                    </span>
                    <span className="shrink-0 rounded-full bg-[#4B6CB7] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      Selected
                    </span>
                  </>
                ) : (
                  <form
                    className="flex min-w-0 flex-1 items-center gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      submitTyped(option);
                    }}
                  >
                    <input
                      type="text"
                      value={typedRole}
                      disabled={hasSelection}
                      maxLength={100}
                      placeholder={
                        option.placeholder ||
                        "Type a role (e.g. Manager, Super Admin)"
                      }
                      aria-label="Type a role"
                      onChange={(event) => setTypedRole(event.target.value)}
                      className="min-w-0 flex-1 rounded-md border border-[#C7D7F5] bg-white px-2.5 py-1.5 text-[13px] font-medium text-[#293F6A] placeholder:font-normal placeholder:text-slate-400 focus:border-[#4B6CB7] focus:outline-none focus:ring-2 focus:ring-[#4B6CB7]/25"
                    />
                    <button
                      type="submit"
                      disabled={hasSelection || !typedRole.trim()}
                      aria-label="Submit role"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <HiArrowRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </form>
                )}
              </div>
            );
          }

          return (
            <button
              key={option.id}
              type="button"
              disabled={hasSelection}
              aria-pressed={isSelected}
              onClick={() => !hasSelection && onSelect?.(option)}
              className={classNames(
                "group/clarify box-border flex items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4B6CB7]/40",
                isSelected
                  ? "border-solid border-[#8fa3d4] bg-[#ECF0FD] text-[#293F6A] shadow-sm"
                  : "border-transparent bg-white shadow-[0_1px_2px_rgba(41,63,106,0.06)] ring-1 ring-slate-200/70",
                !hasSelection &&
                  "hover:border-[#4B6CB7] hover:bg-[#F5F8FF] hover:shadow-[0_4px_14px_-8px_rgba(41,63,106,0.4)] hover:ring-[#C7D7F5]",
                isDimmed && "opacity-55 ring-0",
                hasSelection && "cursor-default pointer-events-none"
              )}
            >
              <span
                className={classNames(
                  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[12px] font-bold transition-colors",
                  isSelected
                    ? "bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] text-white"
                    : "bg-[#ECF0FD] text-[#4B6CB7]",
                  !hasSelection &&
                    "group-hover/clarify:bg-gradient-to-br group-hover/clarify:from-[#4B6CB7] group-hover/clarify:to-[#293F6A] group-hover/clarify:text-white"
                )}
              >
                {isSelected ? (
                  <HiCheck className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  `${index + 1}`
                )}
              </span>
              <span
                className={classNames(
                  "min-w-0 flex-1 truncate text-[13px] font-semibold",
                  isSelected ? "text-[#293F6A]" : "text-slate-700",
                  !hasSelection && "group-hover/clarify:text-[#293F6A]"
                )}
              >
                {option.label}
              </span>
              {isSelected ? (
                <span className="shrink-0 rounded-full bg-[#4B6CB7] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                  Selected
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Typed clarify v1: one or more questions (mcq, true_false, qa, date_range, …).
 * Falls back to legacy chip list when only ``options`` is present.
 */
const CHIP_CLARIFY_TYPES = [
  "person_role",
  "mcq",
  "filter_enum",
  "service_provider",
  "dual_person_role",
  "true_false",
];

const TypedClarifyForm = ({
  questions,
  options,
  answers,
  selectedId,
  selectedLabel,
  onSelect,
  onSubmitAnswers,
}) => {
  const qList = normalizeClarifyQuestions(questions);
  const answerMap = sanitizeClarifyAnswers(answers, qList);
  const hasAnswers =
    (answerMap && Object.keys(answerMap).length > 0) || Boolean(selectedId);
  const [draft, setDraft] = useState({});
  const [step, setStep] = useState(0);

  if (!qList.length) {
    return (
      <ClarifyOptions
        options={options}
        selectedId={selectedId}
        selectedLabel={selectedLabel}
        onSelect={onSelect}
      />
    );
  }

  // Single chip-style question → reuse legacy UX (person_role / mcq / etc.)
  if (
    qList.length === 1 &&
    CHIP_CLARIFY_TYPES.includes(
      String(qList[0].clarify_type || qList[0].clarifyType || "")
    ) &&
    Array.isArray(qList[0].options) &&
    qList[0].options.length
  ) {
    const q = qList[0];
    const qid = resolveClarifyQuestionId(q, 0);
    const ctype = String(q.clarify_type || q.clarifyType);
    const saved = answerMap[qid];
    const opts =
      ctype === "true_false"
        ? [
            { id: "true", label: "Yes", allow_input: false },
            { id: "false", label: "No", allow_input: false },
          ]
        : q.options;
    return (
      <ClarifyOptions
        options={opts}
        selectedId={selectedId || saved?.option_id || saved?.optionId}
        selectedLabel={
          selectedLabel || saved?.text || saved?.selected_label
        }
        onSelect={(option) => {
          const typed = String(option?.typedLabel || "").trim();
          const label = typed || String(option?.label || "").trim();
          const payload = {
            [qid]: {
              option_id: option.id,
              ...(label ? { text: label } : {}),
              ...(ctype === "true_false"
                ? { bool: option.id === "true" || option.id === "yes" }
                : {}),
            },
          };
          onSubmitAnswers?.(payload, qList);
        }}
      />
    );
  }

  const setField = (qid, patch) => {
    if (!qid || qid === "undefined" || qid === "null") return;
    setDraft((prev) => ({ ...prev, [qid]: { ...(prev[qid] || {}), ...patch } }));
  };

  const isAnswered = (q, index) => {
    if (q.required === false) return true;
    const qid = resolveClarifyQuestionId(q, index);
    const a = draft[qid] || answerMap[qid];
    const ctype = String(q.clarify_type || q.clarifyType || "mcq");
    if (!a) return false;
    if (ctype === "qa") return Boolean(String(a.text || "").trim());
    if (ctype === "date_range")
      return Boolean(String(a.from || "").trim() && String(a.to || "").trim());
    return Boolean(a.option_id || a.optionId || a.id);
  };

  const canSubmit = qList.every((q, index) => isAnswered(q, index));
  const safeStep = Math.min(Math.max(step, 0), Math.max(qList.length - 1, 0));
  const q = qList[safeStep];
  const ctype = String(q?.clarify_type || q?.clarifyType || "mcq");
  const qid = q ? resolveClarifyQuestionId(q, safeStep) : "";
  const locked = hasAnswers;
  const saved = answerMap[qid];
  const isLast = safeStep >= qList.length - 1;
  const currentFilled = q ? isAnswered(q, safeStep) : false;

  // Locked summary: show compact answered chips (not the full stacked form).
  if (hasAnswers) {
    return (
      <div className="mt-2.5 overflow-hidden rounded-xl border border-[#DCE6FA] bg-gradient-to-b from-[#F7FAFF] to-white shadow-[0_6px_20px_-14px_rgba(41,63,106,0.35)]">
        <div className="flex items-center gap-2 border-b border-[#E7EEFB] px-3.5 py-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[#ECF0FD] text-[#4B6CB7]">
            <HiCheck className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4B6CB7]">
            Your answers
          </p>
        </div>
        <ul className="flex flex-col gap-1.5 p-3">
          {qList.map((item, index) => {
            const itemId = resolveClarifyQuestionId(item, index);
            const a = answerMap[itemId];
            const t = String(item.clarify_type || item.clarifyType || "");
            let value = "—";
            if (t === "qa") value = String(a?.text || "—");
            else if (t === "date_range")
              value = `${a?.from || "—"} → ${a?.to || "—"}`;
            else if (t === "true_false")
              value =
                a?.bool === true || a?.option_id === "true" ? "Yes" : "No";
            else {
              const oid = String(a?.option_id || a?.optionId || "").trim();
              const fromOpts = (item.options || []).find(
                (opt) => String(opt?.id ?? "").trim() === oid
              );
              value = String(
                a?.text ||
                  a?.selected_label ||
                  fromOpts?.label ||
                  fromOpts?.name ||
                  (oid && !/^(?:region|service_type):\d+$/i.test(oid)
                    ? oid
                    : "") ||
                  "—"
              );
            }
            return (
              <li
                key={itemId}
                className="flex items-start justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200/70"
              >
                <span className="min-w-0 text-[12px] font-medium text-slate-600">
                  {item.question}
                </span>
                <span className="shrink-0 text-[12px] font-semibold text-[#293F6A]">
                  {value}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="mt-2.5 overflow-hidden rounded-xl border border-[#DCE6FA] bg-gradient-to-b from-[#F7FAFF] to-white shadow-[0_6px_20px_-14px_rgba(41,63,106,0.35)]">
      <div className="flex items-center justify-between gap-2 border-b border-[#E7EEFB] px-3.5 py-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-[#ECF0FD] text-[#4B6CB7]">
            <HiOutlineQuestionMarkCircle className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4B6CB7]">
            Question {safeStep + 1} of {qList.length}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {qList.map((item, idx) => (
            <span
              key={resolveClarifyQuestionId(item, idx)}
              className={classNames(
                "h-1.5 w-1.5 rounded-full",
                idx === safeStep
                  ? "bg-[#4B6CB7]"
                  : isAnswered(item, idx)
                    ? "bg-[#8fa3d4]"
                    : "bg-slate-200"
              )}
              aria-hidden="true"
            />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3 p-3">
        <p className="text-[13px] font-semibold text-[#293F6A]">{q?.question}</p>

        {ctype === "qa" ? (
          <input
            type="text"
            disabled={locked}
            maxLength={200}
            placeholder={q?.placeholder || "Type your answer"}
            value={
              locked
                ? String(saved?.text || "")
                : String(draft[qid]?.text || "")
            }
            onChange={(e) => setField(qid, { text: e.target.value })}
            className="rounded-md border border-[#C7D7F5] bg-white px-2.5 py-1.5 text-[13px] text-[#293F6A] focus:border-[#4B6CB7] focus:outline-none focus:ring-2 focus:ring-[#4B6CB7]/25"
          />
        ) : null}

        {ctype === "date_range" ? (
          <div className="flex flex-wrap gap-2">
            <input
              type="date"
              disabled={locked}
              value={
                locked
                  ? String(saved?.from || "")
                  : String(draft[qid]?.from || "")
              }
              onChange={(e) => setField(qid, { from: e.target.value })}
              className="rounded-md border border-[#C7D7F5] bg-white px-2.5 py-1.5 text-[13px] text-[#293F6A]"
            />
            <input
              type="date"
              disabled={locked}
              value={
                locked ? String(saved?.to || "") : String(draft[qid]?.to || "")
              }
              onChange={(e) => setField(qid, { to: e.target.value })}
              className="rounded-md border border-[#C7D7F5] bg-white px-2.5 py-1.5 text-[13px] text-[#293F6A]"
            />
          </div>
        ) : null}

        {CHIP_CLARIFY_TYPES.includes(ctype) ? (
          <ClarifyOptions
            options={
              ctype === "true_false"
                ? [
                    { id: "true", label: "Yes", allow_input: false },
                    { id: "false", label: "No", allow_input: false },
                  ]
                : q?.options || []
            }
            selectedId={
              locked
                ? saved?.option_id || saved?.optionId
                : draft[qid]?.option_id
            }
            selectedLabel={locked ? saved?.text : draft[qid]?.text}
            onSelect={(option) => {
              if (locked) return;
              const typed = String(option?.typedLabel || "").trim();
              const label = typed || String(option?.label || "").trim();
              setField(qid, {
                option_id: option.id,
                ...(label ? { text: label } : {}),
                ...(ctype === "true_false"
                  ? { bool: option.id === "true" }
                  : {}),
              });
            }}
          />
        ) : null}

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            disabled={safeStep <= 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-1 rounded-lg border border-[#C7D7F5] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#293F6A] transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
          >
            <HiChevronLeft className="h-4 w-4" aria-hidden="true" />
            Previous
          </button>

          <div className="flex items-center gap-2">
            {!isLast ? (
              <button
                type="button"
                disabled={!currentFilled}
                onClick={() =>
                  setStep((s) => Math.min(qList.length - 1, s + 1))
                }
                className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] px-3.5 py-1.5 text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
                <HiChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : null}
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() =>
                onSubmitAnswers?.(
                  sanitizeClarifyAnswers(draft, qList),
                  qList
                )
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-br from-[#4B6CB7] to-[#293F6A] px-3.5 py-1.5 text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              Confirm
              <HiArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SNIPPET_PREVIEW_CHARS = 180;

/** Only entries that describe a real citation are shown (drop usage/billing markers). */
const isDisplaySource = (source) =>
  source &&
  typeof source === "object" &&
  source.kind !== "usage" &&
  Boolean(source.title || source.url);

/** Plain text for Sources — strip leftover markdown from older messages. */
const cleanSourceText = (text) =>
  String(text || "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/\*\*|__|`+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const truncateSnippet = (text, max = SNIPPET_PREVIEW_CHARS) => {
  const value = cleanSourceText(text);
  if (!value) return "";
  if (value.length <= max) return value;
  const cut = value.slice(0, max).replace(/\s+\S*$/, "").trim();
  return `${cut || value.slice(0, max)}…`;
};

/** Keep Sources that match inline [n] citations in the answer (backup to server filter). */
const filterSourcesToCited = (sources, answerContent) => {
  const list = Array.isArray(sources) ? sources.filter(isDisplaySource) : [];
  if (!list.length) return [];
  const cited = [
    ...String(answerContent || "").matchAll(/\[(\d+)\]/g),
  ].map((m) => Number(m[1]));
  if (!cited.length) return list;
  const set = new Set(cited);
  const filtered = list.filter((s) => set.has(Number(s.n)));
  return filtered.length ? filtered : list;
};

/** True when the answer was grounded in live web results (web-search lane). */
const usedWebSearch = (sources) =>
  Array.isArray(sources) &&
  sources.some((s) => s && s.kind === "web" && s.url);

/** Small pill shown above a web-search answer so it's clear the web was used. */
const WebSearchBadge = () => (
  <span className="mb-1.5 inline-flex items-center justify-center gap-1 rounded-full border border-[#DCE6FA] bg-[#F5F8FF] px-2 py-0.5 text-[10.5px] font-semibold uppercase leading-none tracking-wide text-[#4B6CB7]">
    <HiOutlineGlobeAlt className="h-3 w-3 shrink-0" aria-hidden="true" />
    <span className="leading-none">Searched the web</span>
  </span>
);

/** Subtle Phase 0 degrade signal — not an error, just reduced-mode honesty. */
const ReducedModeBadge = ({ degradeReason, servicePath }) => {
  const { label, detail } = getCopilotReducedModeCopy(degradeReason, servicePath);
  return (
    <span
      className="mb-1.5 inline-flex max-w-full items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10.5px] font-medium leading-snug text-slate-600"
      title={detail}
    >
      <HiOutlineQuestionMarkCircle
        className="h-3 w-3 shrink-0 text-slate-500"
        aria-hidden="true"
      />
      <span className="leading-none">{label}</span>
    </span>
  );
};

const SourcePassage = ({ source }) => {
  const [expanded, setExpanded] = useState(false);
  const full = cleanSourceText(source.text || source.snippet);
  const preview = truncateSnippet(source.snippet || source.text);
  if (!full && !preview) return null;
  const canExpand = full.length > SNIPPET_PREVIEW_CHARS;
  const shown = expanded || !canExpand ? full || preview : preview;

  return (
    <span className="mt-0.5 block text-[11.5px] text-slate-500">
      {shown}
      {canExpand ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="ml-1 inline font-semibold text-[#4B6CB7] hover:text-[#293F6A]"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </span>
  );
};

/**
 * Numbered reference list rendered under an assistant answer. The order matches
 * the inline [1], [2], … citation markers in the text. Sources that carry a URL
 * become clickable links (open in a new tab); internal knowledge-base / guide
 * citations (no URL) show the cited passage so the reference is still meaningful.
 */
const MessageSources = ({ sources, answerContent }) => {
  const items = filterSourcesToCited(sources, answerContent);
  if (!items.length) return null;
  const isWeb = usedWebSearch(items);
  const HeaderIcon = isWeb ? HiOutlineGlobeAlt : HiOutlineBookOpen;
  const headerLabel = isWeb
    ? items.length === 1
      ? "Web source"
      : "Web sources"
    : items.length === 1
      ? "Source"
      : "Sources";

  return (
    <div className="mt-2.5 rounded-xl border border-slate-200 bg-slate-50/70 px-3.5 py-2.5">
      <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <HeaderIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {headerLabel}
      </p>
      <ol className="space-y-1.5">
        {items.map((source, index) => {
          const number = source.n || index + 1;
          const label = source.title || source.url;
          return (
            <li
              key={`${number}-${index}`}
              className="flex gap-2 text-[12.5px] leading-snug text-slate-600"
            >
              <span className="mt-[1px] inline-flex h-4 min-w-[16px] items-center justify-center rounded bg-[#ECF0FD] px-1 text-[10px] font-bold text-[#4B6CB7]">
                {number}
              </span>
              <span className="min-w-0 flex-1">
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-[#4B6CB7] underline decoration-[#C7D7F5] underline-offset-2 transition-colors hover:text-[#293F6A]"
                  >
                    <span className="break-words">{label}</span>
                    <HiOutlineExternalLink
                      className="h-3 w-3 shrink-0"
                      aria-hidden="true"
                    />
                  </a>
                ) : (
                  <span className="font-medium text-slate-700 break-words">
                    {label}
                  </span>
                )}
                <SourcePassage source={source} />
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const ChatMessage = ({
  message,
  isStreaming = false,
  isLastAssistant = false,
  onFollowUp,
  onRetry,
  retryPrompt,
  onClarifyOption,
  clarifyPrompt,
  conversationId = null,
  onMessageRated = null,
}) => {
  const isUser = message.role === "user";
  const timestamp = formatChatTimestamp(message.createdAt);
  const targetContent = String(message.content || "");
  const hasContent = Boolean(targetContent.trim());
  const isError = Boolean(message.isError);
  // Typewriter only while tokens are actively streaming — never on history reload
  // or when revisiting a finished chat (remount would otherwise replay the effect).
  const shouldAnimateText = !isError && isStreaming;
  const revealedContent = useTypewriterReveal(targetContent, shouldAnimateText);
  const showStreamingCursor =
    !isError && revealedContent.length < targetContent.length;

  if (!isUser && !hasContent && !isStreaming && !isError) {
    return null;
  }

  if (isUser) {
    const { base, clarifications } = parseClarifyUserContent(message.content);
    const showClarifyCard = clarifications.length > 0;
    return (
      <div className="copilot-message-enter flex flex-col items-end gap-1.5">
        <div className="flex items-start gap-2.5">
          <div className="flex min-w-0 max-w-[88%] flex-col items-end gap-1.5">
            {base ? (
              <div
                className={classNames(
                  "rounded-2xl rounded-tr-md px-4 py-2 text-[13.5px] leading-relaxed",
                  VERTEX360_COPILOT_THEME.userCard
                )}
              >
                <p className="whitespace-pre-wrap break-words">{base}</p>
              </div>
            ) : null}
            {showClarifyCard ? (
              <div
                className={classNames(
                  "w-full min-w-[220px] overflow-hidden rounded-2xl rounded-tr-md border border-white/25 bg-white/15 px-3 py-2.5 text-left shadow-sm backdrop-blur-[2px]",
                  VERTEX360_COPILOT_THEME.userCard
                )}
              >
                <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/80">
                  Your clarifications
                </p>
                <ul className="flex flex-col gap-1">
                  {clarifications.map((item, idx) => (
                    <li
                      key={`${item.label}-${idx}`}
                      className="flex items-baseline justify-between gap-3 rounded-lg bg-white/15 px-2.5 py-1.5"
                    >
                      <span className="text-[11px] font-medium text-white/75">
                        {item.label}
                      </span>
                      <span className="text-[12px] font-semibold text-white">
                        {item.value}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : !base ? (
              <div
                className={classNames(
                  "rounded-2xl rounded-tr-md px-4 py-2 text-[13.5px] leading-relaxed",
                  VERTEX360_COPILOT_THEME.userCard
                )}
              >
                <p className="whitespace-pre-wrap break-words">{message.content}</p>
              </div>
            ) : null}
            {timestamp ? (
              <time
                dateTime={message.createdAt}
                className="mt-0.5 px-1 text-[11px] text-gray-500"
              >
                {timestamp}
              </time>
            ) : null}
          </div>
          <UserAvatar />
        </div>
      </div>
    );
  }

  const showWebBadge = !isError && usedWebSearch(message.sources);
  const showReducedMode =
    !isError && !isStreaming && Boolean(message.isReducedMode);

  return (
    <div className="group copilot-message-enter flex items-start gap-3">
      <Vertex360CopilotAiIcon animated={isStreaming} size="md" />
      <div className="min-w-0 flex-1">
        {showWebBadge ? <WebSearchBadge /> : null}
        {showReducedMode ? (
          <ReducedModeBadge
            degradeReason={message.degradeReason}
            servicePath={message.servicePath}
          />
        ) : null}
        <div
          className={classNames(
            "rounded-2xl rounded-tl-md px-4 py-2 transition-shadow duration-200",
            isError ? VERTEX360_COPILOT_THEME.answerCardError : VERTEX360_COPILOT_THEME.answerCard,
            !isStreaming && !isError && VERTEX360_COPILOT_THEME.answerCardHover
          )}
        >
          {hasContent ? (
            isError ? (
              <p className="text-[13.5px] leading-relaxed">{message.content}</p>
            ) : Array.isArray(message.render?.questions) &&
              message.render.questions.length > 1 &&
              message.render?.type === "clarify" ? (
              <p className="text-[13.5px] leading-relaxed text-slate-700">
                I need a few details to continue — answer one question at a
                time below.
              </p>
            ) : (
              <MarkdownMessage
                content={revealedContent}
                tone="light"
              />
            )
          ) : (
            <div className="h-4" aria-hidden="true" />
          )}
          {showStreamingCursor ? <StreamingCursor /> : null}
        </div>

        {!isError &&
        (message.render?.type === "table" ||
          message.render?.ui === "accordion" ||
          (Array.isArray(message.render?.groups) &&
            message.render.groups.length > 0) ||
          (Array.isArray(message.render?.columns) &&
            Array.isArray(message.render?.rows) &&
            message.render.rows.length > 0)) ? (
          <RenderResult render={message.render} />
        ) : null}

        {!isError &&
        message.render?.type === "clarify" &&
        !message.render?.dismissed &&
        message.render?.selected !== "__abandoned__" &&
        ((Array.isArray(message.render.questions) &&
          message.render.questions.length > 0) ||
          (Array.isArray(message.render.options) &&
            message.render.options.length > 0)) ? (
          <TypedClarifyForm
            questions={message.render.questions}
            options={message.render.options}
            answers={message.render.answers}
            selectedId={message.render.selected}
            selectedLabel={
              message.render.selected_label || message.render.selectedLabel
            }
            onSelect={(option) =>
              onClarifyOption?.(clarifyPrompt, option, message)
            }
            onSubmitAnswers={(answers, questions) =>
              onClarifyOption?.(clarifyPrompt, { answers, questions }, message)
            }
          />
        ) : null}

        {!isError && !isStreaming ? (
          <MessageSources
            sources={message.sources}
            answerContent={message.content}
          />
        ) : null}

        {isError && retryPrompt && onRetry ? (
          <button
            type="button"
            onClick={() => onRetry(retryPrompt)}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 shadow-sm transition-colors hover:border-red-300 hover:bg-red-50"
          >
            <HiOutlineRefresh className="h-3.5 w-3.5" aria-hidden />
            Retry
          </button>
        ) : null}

        {!isStreaming && (timestamp || (!isError && hasContent)) ? (
          <div className="mt-1 flex items-center gap-1 px-1">
            {timestamp ? (
              <time
                dateTime={message.createdAt}
                className="text-[11px] text-gray-500"
              >
                {timestamp}
              </time>
            ) : null}
            {!isError && hasContent ? (
              <MessageActions
                content={message.content}
                showRegenerate={Boolean(isLastAssistant && retryPrompt && onRetry)}
                onRegenerate={
                  isLastAssistant && retryPrompt && onRetry
                    ? () => onRetry(retryPrompt)
                    : undefined
                }
              />
            ) : null}
          </div>
        ) : null}

        {!isError &&
        !isStreaming &&
        isLastAssistant &&
        Array.isArray(message.suggestions) &&
        message.suggestions.length &&
        onFollowUp ? (
          <FollowUpChips
            suggestions={message.suggestions}
            onSelect={onFollowUp}
          />
        ) : null}
      </div>
    </div>
  );
};

export default ChatMessage;
